import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useActiveSession } from '../api/active/sessionProvider';
import {
  createFormalWorkspaceSessionScope,
  formalWorkspaceSessionStorageKey,
} from '../quote-integration/formalWorkspaceSession';
import {
  createMarketingAnnouncementApi,
  type MarketingAnnouncement,
} from './marketingAnnouncementApi';

export type AnnouncementReadState =
  | { status: 'loading' }
  | { status: 'ready'; value: MarketingAnnouncement }
  | { status: 'error'; message: string };

type PrefetchValue = {
  read: AnnouncementReadState;
  reload(): void;
};

const Context = createContext<PrefetchValue | null>(null);

/** Start the scoped announcement read as soon as the authenticated session is ready. */
export function MarketingAnnouncementPrefetchProvider({
  children,
}: {
  children: ReactNode;
}) {
  const session = useActiveSession();
  const scopeKey =
    !session.offlineDemoEnabled && session.state.status === 'ready'
      ? formalWorkspaceSessionStorageKey(
          createFormalWorkspaceSessionScope(session.state.user),
        )
      : null;
  const [result, setResult] = useState<{
    key: string;
    read: AnnouncementReadState;
  } | null>(null);
  const requestRef = useRef<AbortController | null>(null);
  const api = useMemo(
    () => createMarketingAnnouncementApi(session.transport, null),
    [session.transport],
  );
  const reload = useCallback(() => {
    requestRef.current?.abort();
    if (!scopeKey) return;
    const controller = new AbortController();
    requestRef.current = controller;
    setResult({ key: scopeKey, read: { status: 'loading' } });
    void api.get(controller.signal).then(
      (value) => {
        if (!controller.signal.aborted)
          setResult({ key: scopeKey, read: { status: 'ready', value } });
      },
      (cause) => {
        if (!controller.signal.aborted)
          setResult({
            key: scopeKey,
            read: {
              status: 'error',
              message:
                cause instanceof Error ? cause.message : '营销说明读取失败',
            },
          });
      },
    );
  }, [api, scopeKey]);

  useEffect(() => {
    reload();
    return () => requestRef.current?.abort();
  }, [reload]);

  const read =
    result?.key === scopeKey ? result.read : { status: 'loading' as const };
  return (
    <Context.Provider value={{ read, reload }}>{children}</Context.Provider>
  );
}

export function useOptionalMarketingAnnouncementPrefetch() {
  return useContext(Context);
}
